import React, { useState, useMemo } from 'react';

// Wizard-Schrittdefinition (Reihenfolge: Montage zuerst)
const STEP_DEFS = [
  { key: 'mounting', title: 'Wie soll der Mast montiert werden?', subtitle: 'Wählen Sie die gewünschte Montageart' },
  { key: 'type', title: 'Welchen Masttyp benötigen Sie?', subtitle: 'Starrer oder kippbarer Kameramast' },
  { key: 'height', title: 'Welche Masthöhe benötigen Sie?', subtitle: 'Gesamthöhe des Kameramasts' },
  { key: 'diameter', title: 'Welchen Durchmesser benötigen Sie?', subtitle: 'Rohrdurchmesser des Mastes' },
  { key: 'tilt_mechanism', title: 'Welche Kippvariante?', subtitle: 'Nur bei kippbaren Masten relevant', onlyIf: (s) => s.type === 'kippbar' },
  { key: 'variant', title: 'Welche Aufsatz-Variante?', subtitle: 'Form des Mastkopfes' },
  { key: 'cable_type', title: 'Welche Kabelführung?', subtitle: 'Art der Kabelführung im Mast' },
];

const LABELS = {
  mounting: { erdstück: 'Erdstück', fußplatte: 'Fußplatte' },
  type: { starre: 'Starrer Mast', kippbar: 'Kippbarer Mast' },
  tilt_mechanism: { winde: 'Mit Winde', keil: 'Mit Keil' },
  variant: { konisch: 'Konisch', zylindrisch: 'Zylindrisch' },
  cable_type: { fester_zopf: 'Fester Zopf', loser_zopf: 'Loser Zopf' },
};

const DESCRIPTIONS = {
  mounting: { erdstück: 'Zum Einbetonieren im Boden', fußplatte: 'Zur Verschraubung auf Fundament' },
  type: { starre: 'Feste Bauweise ohne Kippfunktion', kippbar: 'Zum Herunterklappen für Wartung' },
  tilt_mechanism: { winde: 'Komfortables Kippen per Winde', keil: 'Kippen über Keilmechanismus' },
  variant: { konisch: 'Sich verjüngender Aufsatz', zylindrisch: 'Zylindrisch abgesetzter Aufsatz' },
  cable_type: { fester_zopf: 'Fest verschweißter Zopf', loser_zopf: 'Abnehmbarer Zopf' },
};

const ORDER = {
  mounting: ['erdstück', 'fußplatte'],
  type: ['starre', 'kippbar'],
  tilt_mechanism: ['winde', 'keil'],
  variant: ['konisch', 'zylindrisch'],
  cable_type: ['fester_zopf', 'loser_zopf'],
};

const numeric = (v) => parseFloat(String(v).replace(/[^\d.]/g, '')) || 0;

const TOOLTIPS = {
  mounting: {
    erdstück: 'Der Mast ist ca. 0,5–0,8 m länger als die lichte Höhe. Er wird in ein Rohr gesetzt und versandet.',
    fußplatte: 'Es wird ein Fundamentanker benötigt und gesetzt. Das Betonfundament sollte 1×1×1 m betragen; der Mast wird per Fußplatte festgeschraubt.',
  },
  tilt_mechanism: {
    winde: 'Das Innere der Mastöffnung wird durch die Winde besetzt. Dadurch ist bei kleineren Längen mit einer Mastklappe kein Platz für einen innenmontierten Anschaltkasten.',
  },
  cable_type: {
    loser_zopf: 'Zopf oben abnehmbar – dadurch lässt sich die Kameramontage entspannt am Arbeitsplatz vornehmen.',
    fester_zopf: 'Die Montage der Kameras hat direkt am Mast zu erfolgen.',
  },
};

const PoleSelection = ({ poles, configuredPoles = [], onAddPole, onRemovePole, onProceed }) => {
  const [selections, setSelections] = useState({});
  const [stepIndex, setStepIndex] = useState(0);
  const [quantities, setQuantities] = useState({});
  const [openTip, setOpenTip] = useState(null);

  const totalConfiguredMasts = configuredPoles.reduce((sum, e) => sum + (e.quantity || 1), 0);

  // Aktive Schritte (Kippmechanismus nur bei kippbarem Mast)
  const activeSteps = useMemo(
    () => STEP_DEFS.filter((d) => !d.onlyIf || d.onlyIf(selections)),
    [selections]
  );

  // Masten, die zu den Auswahlen der VORHERIGEN Schritte passen
  // (aktueller Schritt wird ausgeklammert, damit beim Zurückgehen alle Optionen erscheinen)
  const priorKeys = useMemo(
    () => activeSteps.slice(0, stepIndex).map((s) => s.key),
    [activeSteps, stepIndex]
  );

  const remainingPoles = useMemo(
    () =>
      poles.filter((p) => priorKeys.every((k) => !selections[k] || p[k] === selections[k])),
    [poles, selections, priorKeys]
  );

  const isFinished = stepIndex >= activeSteps.length;
  const currentStep = activeSteps[stepIndex];

  // Optionen für den aktuellen Schritt inkl. Anzahl passender Masten
  const options = useMemo(() => {
    if (!currentStep) return [];
    const key = currentStep.key;
    const counts = {};
    remainingPoles.forEach((p) => {
      const val = p[key];
      if (val === null || val === undefined) return;
      counts[val] = (counts[val] || 0) + 1;
    });
    let values = Object.keys(counts);
    if (ORDER[key]) {
      values.sort((a, b) => ORDER[key].indexOf(a) - ORDER[key].indexOf(b));
    } else {
      values.sort((a, b) => numeric(a) - numeric(b));
    }
    return values.map((value) => ({ value, count: counts[value] }));
  }, [currentStep, remainingPoles]);

  const handleSelectOption = (key, value) => {
    // Auswahl setzen und alle nachfolgenden Auswahlen zurücksetzen
    const idx = activeSteps.findIndex((s) => s.key === key);
    const keysToClear = activeSteps.slice(idx + 1).map((s) => s.key);
    setSelections((prev) => {
      const next = { ...prev, [key]: value };
      keysToClear.forEach((k) => delete next[k]);
      // Kippmechanismus verwerfen, wenn Masttyp nicht kippbar
      if (key === 'type' && value !== 'kippbar') delete next.tilt_mechanism;
      return next;
    });
    setStepIndex(idx + 1);
  };

  const handleBack = () => setStepIndex((i) => Math.max(0, i - 1));

  const handleReset = () => {
    setSelections({});
    setStepIndex(0);
    setQuantities({});
  };

  const formatValue = (key, value) =>
    LABELS[key] ? LABELS[key][value] || value : value;

  const totalSteps = activeSteps.length;
  const progress = Math.round((Math.min(stepIndex, totalSteps) / totalSteps) * 100);

  return (
    <div className="animate-slideIn max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-3 drop-shadow-lg">
          Mast-Konfigurator
        </h2>
        <p className="text-slate-100 max-w-2xl mx-auto drop-shadow">
          Beantworten Sie Schritt für Schritt einige kurze Fragen – wir führen Sie
          direkt zu Ihrem passenden Kameramast.
        </p>
      </div>

      {/* Bereits konfigurierte Masten */}
      {configuredPoles.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-5 mb-6 border-2 border-green-200" data-testid="configured-poles-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">
              Ihre konfigurierten Masten
              <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full" data-testid="configured-poles-total">
                Gesamt: {totalConfiguredMasts} Stück
              </span>
            </h3>
            <button
              onClick={onProceed}
              className="btn btn-primary"
              data-testid="proceed-to-accessories-btn"
            >
              Weiter zum Zubehör
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <div className="space-y-2">
            {configuredPoles.map((entry, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                data-testid={`configured-pole-${idx}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{entry.pole.name}</p>
                  <p className="text-xs text-slate-500">
                    {entry.pole.height} • {entry.pole.diameter} • {entry.pole.mounting === 'erdstück' ? 'Erdstück' : 'Fußplatte'} • Menge: {entry.quantity}
                  </p>
                </div>
                <button
                  onClick={() => onRemovePole(idx)}
                  className="ml-3 text-sm text-red-600 hover:text-red-800 font-medium flex-shrink-0"
                  data-testid={`remove-pole-${idx}`}
                >
                  Entfernen
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Sie können unten einen weiteren Mast konfigurieren oder direkt mit dem Zubehör fortfahren.
          </p>
        </div>
      )}


      {/* Fortschritt + Live-Zähler */}
      <div className="bg-white rounded-xl shadow-lg p-5 mb-6" data-testid="wizard-progress">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-slate-700">
            Schritt {Math.min(stepIndex + 1, totalSteps)} von {totalSteps}
          </span>
          <span
            className="inline-flex items-center gap-2 text-sm font-semibold xortec-text-accent"
            data-testid="wizard-remaining-count"
          >
            <span className="w-2 h-2 rounded-full bg-blue-600"></span>
            Noch {remainingPoles.length} passende{remainingPoles.length === 1 ? 'r' : ''} Mast
            {remainingPoles.length === 1 ? '' : 'en'}
          </span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* Bisherige Auswahl als Chips */}
        {Object.keys(selections).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {activeSteps
              .slice(0, stepIndex)
              .filter((s) => selections[s.key])
              .map((s) => (
                <span
                  key={s.key}
                  className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full"
                  data-testid={`wizard-chip-${s.key}`}
                >
                  {formatValue(s.key, selections[s.key])}
                </span>
              ))}
          </div>
        )}
      </div>

      {/* Frage-Schritt */}
      {!isFinished && currentStep && (
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8" data-testid={`wizard-step-${currentStep.key}`}>
          <div className="mb-6">
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              {currentStep.title}
            </h3>
            <p className="text-sm text-slate-500 mt-1">{currentStep.subtitle}</p>
            {currentStep.key === 'height' && (
              <p className="text-sm text-blue-700 mt-2" data-testid="height-info">
                Sonderhöhen können jederzeit angefragt werden.
              </p>
            )}
          </div>

          {currentStep.key === 'diameter' && options.some((o) => o.value === '250mm') && (
            <div
              className="flex items-start gap-3 mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200"
              data-testid="diameter-weight-hint"
            >
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <p className="text-sm text-amber-800">
                <span className="font-semibold">Hinweis:</span> Bei einem Gesamtgewicht am Zopf von mehr als
                {' '}<span className="font-semibold">10&nbsp;kg</span> empfehlen wir einen Durchmesser von
                {' '}<span className="font-semibold">250&nbsp;mm</span>.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {options.map((opt) => {
              const tip = TOOLTIPS[currentStep.key]?.[opt.value];
              const tipId = `${currentStep.key}-${opt.value}`;
              return (
              <button
                key={opt.value}
                onClick={() => handleSelectOption(currentStep.key, opt.value)}
                className="relative group text-left p-5 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all duration-200"
                data-testid={`wizard-option-${currentStep.key}-${opt.value}`}
              >
                {tip && (
                  <span
                    className="absolute top-3 right-3 z-20"
                    onMouseEnter={() => setOpenTip(tipId)}
                    onMouseLeave={() => setOpenTip((c) => (c === tipId ? null : c))}
                    onClick={(e) => { e.stopPropagation(); setOpenTip((c) => (c === tipId ? null : tipId)); }}
                    data-testid={`option-info-${tipId}`}
                  >
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs font-bold hover:bg-blue-600 hover:text-white transition-colors">i</span>
                    {openTip === tipId && (
                      <span
                        className="absolute right-0 top-7 w-64 p-3 text-xs leading-relaxed text-white bg-slate-800 rounded-lg shadow-xl text-left font-normal normal-case z-30"
                        data-testid={`option-tooltip-${tipId}`}
                      >
                        {tip}
                      </span>
                    )}
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-slate-900 group-hover:text-blue-700">
                    {formatValue(currentStep.key, opt.value)}
                  </span>
                  <span className={`text-xs font-medium text-slate-400 bg-slate-100 px-2 py-1 rounded-full group-hover:bg-blue-100 group-hover:text-blue-700 ${tip ? 'mr-7' : ''}`}>
                    {opt.count} Mast{opt.count === 1 ? '' : 'en'}
                  </span>
                </div>
                {DESCRIPTIONS[currentStep.key]?.[opt.value] && (
                  <p className="text-sm text-slate-500 mt-2">
                    {DESCRIPTIONS[currentStep.key][opt.value]}
                  </p>
                )}
              </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={handleBack}
              disabled={stepIndex === 0}
              className={`btn ${stepIndex === 0 ? 'btn-disabled' : 'btn-secondary'}`}
              data-testid="wizard-back-btn"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Zurück
            </button>
            <button
              onClick={handleReset}
              className="text-sm xortec-text-accent hover:underline font-medium"
              data-testid="wizard-reset-btn"
            >
              Neu starten
            </button>
          </div>
        </div>
      )}

      {/* Ergebnis */}
      {isFinished && (
        <div data-testid="wizard-result">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-green-900">
                {remainingPoles.length === 1
                  ? 'Wir haben den passenden Mast für Sie gefunden!'
                  : `${remainingPoles.length} Masten passen zu Ihrer Auswahl`}
              </p>
              <p className="text-sm text-green-700">
                Wählen Sie die gewünschte Menge und fahren Sie mit dem Zubehör fort.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {remainingPoles.map((pole) => (
              <div key={pole.id} className="pole-card card" data-testid={`wizard-result-card-${pole.id}`}>
                <div className="p-6">
                  {pole.image_url && (
                    <div className="pole-image-container mb-4">
                      <img
                        src={pole.image_url}
                        alt={pole.name}
                        className="pole-card-image"
                        onError={(e) => {
                          e.target.closest('.pole-image-container').style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <span className={`pole-badge ${pole.type === 'starre' ? 'badge-starre' : 'badge-kippbar'}`}>
                    {pole.type === 'starre' ? 'Starrer Mast' : 'Kippbarer Mast'}
                  </span>
                  <h3 className="text-lg font-bold text-slate-900 mt-3 mb-2 xortec-text-primary">
                    {pole.name}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">{pole.description}</p>

                  <div className="spec-grid">
                    <div className="spec-item"><span className="spec-label">Höhe</span><span className="spec-value">{pole.height}</span></div>
                    <div className="spec-item"><span className="spec-label">Durchmesser</span><span className="spec-value">{pole.diameter}</span></div>
                    <div className="spec-item"><span className="spec-label">Aufsatz</span><span className="spec-value">{pole.variant === 'konisch' ? 'Konisch' : 'Zylindrisch'}</span></div>
                    <div className="spec-item"><span className="spec-label">Montage</span><span className="spec-value">{pole.mounting === 'erdstück' ? 'Erdstück' : 'Fußplatte'}</span></div>
                    <div className="spec-item"><span className="spec-label">Kabelführung</span><span className="spec-value">{pole.cable_type === 'fester_zopf' ? 'Fester Zopf' : 'Loser Zopf'}</span></div>
                    {pole.tilt_mechanism && (
                      <div className="spec-item"><span className="spec-label">Kippmechanismus</span><span className="spec-value">{pole.tilt_mechanism === 'winde' ? 'Mit Winde' : 'Mit Keil'}</span></div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <span className="text-xs text-slate-500">Art.-Nr.: {pole.article_number}</span>
                  </div>

                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium text-slate-700" htmlFor={`wizard-quantity-input-${pole.id}`}>Menge:</label>
                      <input
                        id={`wizard-quantity-input-${pole.id}`}
                        type="number"
                        min="1"
                        step="1"
                        className="w-24 px-3 py-1 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={quantities[pole.id] || 1}
                        onChange={(e) => {
                          const raw = parseInt(e.target.value, 10);
                          const val = Number.isFinite(raw) && raw > 0 ? raw : 1;
                          setQuantities((prev) => ({ ...prev, [pole.id]: val }));
                        }}
                        data-testid={`wizard-quantity-select-${pole.id}`}
                      />
                    </div>
                    <button
                      onClick={() => {
                        onAddPole(pole, quantities[pole.id] || 1);
                        handleReset();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full btn btn-secondary"
                      data-testid={`wizard-add-another-${pole.id}`}
                    >
                      + Hinzufügen &amp; weiteren Mast konfigurieren
                    </button>
                    <button
                      onClick={() => {
                        onAddPole(pole, quantities[pole.id] || 1);
                        onProceed();
                      }}
                      className="w-full btn btn-primary"
                      data-testid={`wizard-result-select-${pole.id}`}
                    >
                      Hinzufügen &amp; weiter zum Zubehör
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-8">
            <button onClick={handleBack} className="btn btn-secondary" data-testid="wizard-back-btn-result">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Zurück
            </button>
            <button onClick={handleReset} className="text-sm xortec-text-accent hover:underline font-medium" data-testid="wizard-reset-btn-result">
              Neu starten
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoleSelection;
